import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class FmrCartUpsell extends HTMLElement {
  #pending = false;

  connectedCallback() {
    this.addEventListener('change', this.#changeVariant);
    this.addEventListener('submit', this.#add);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#changeVariant);
    this.removeEventListener('submit', this.#add);
  }

  #changeVariant = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.form) return;
    const option = select.selectedOptions[0];
    if (!option?.value) return;
    const form = select.form;
    form.elements.namedItem('quantity').value = option.dataset.quantity;
    form.querySelector('[data-upsell-price]').textContent = `— ${option.dataset.price}`;
    const quantityLabel = form.querySelector('[data-upsell-quantity]');
    quantityLabel.hidden = Number(option.dataset.quantity) === 1;
    quantityLabel.textContent = `× ${option.dataset.quantity}`;
    const image = form.closest('.fmr-upsell__card').querySelector('img');
    if (image && option.dataset.image) {
      image.removeAttribute('srcset');
      image.src = option.dataset.image;
    }
    form.querySelector('[role="alert"]').hidden = true;
  };

  #add = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();
    if (this.#pending) return;

    const button = form.querySelector('button[type="submit"]');
    const select = form.querySelector('.fmr-upsell__variant');
    if (select?.hidden) {
      this.querySelectorAll('.fmr-upsell__variant').forEach((variantSelect) => {
        const expanded = variantSelect === select;
        variantSelect.hidden = !expanded;
        variantSelect.required = expanded;
        variantSelect.form.querySelector('button[type="submit"]').setAttribute('aria-expanded', String(expanded));
      });
      select.focus({ preventScroll: true });
      return;
    }
    const label = button.querySelector('[data-upsell-add]');
    const originalLabel = label.textContent;
    const restoreFocus = document.activeElement === button;
    const error = form.querySelector('[role="alert"]');
    const drawer = this.closest('cart-drawer-component');
    const placement = this.classList.contains('fmr-upsell--desktop') ? 'desktop' : 'mobile';
    const sectionIds = new Set([this.closest('cart-items-component').dataset.sectionId]);
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((cart) => sectionIds.add(cart.dataset.sectionId));
    const body = new FormData(form);
    body.set('sections', Array.from(sectionIds).slice(0, 5).join(','));
    body.set('sections_url', window.location.pathname);

    this.#pending = true;
    error.hidden = true;
    error.textContent = '';
    this.querySelectorAll('button, select').forEach((control) => { control.disabled = true; });
    button.setAttribute('aria-busy', 'true');
    label.textContent = this.dataset.loading;
    let errorMessage = this.dataset.error;

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('javascript', { body }));
      const result = await response.json();
      if (!response.ok || result.status) {
        if (typeof result.description === 'string') errorMessage = result.description;
        throw new Error(errorMessage);
      }

      const addedMessage = this.dataset.added;
      this.dispatchEvent(new CartAddEvent(result, form.id, {
        source: 'fmr-cart-upsell',
        variantId: body.get('id').toString(),
        sections: result.sections,
      }));
      requestAnimationFrame(() => {
        if (!drawer?.querySelector('dialog')?.open) return;
        const liveRegion = drawer.querySelector('[ref="liveRegion"]');
        if (liveRegion) liveRegion.textContent = addedMessage;
        if (restoreFocus && !button.isConnected) {
          const nextButton = drawer.querySelector(`.fmr-upsell--${placement} button[type="submit"]`);
          (nextButton || drawer.querySelector('#cart-drawer-heading'))?.focus({ preventScroll: true });
        }
      });
    } catch {
      error.textContent = errorMessage;
      error.hidden = false;
      this.dispatchEvent(new CartErrorEvent(form.id, this.dataset.error, errorMessage, {}));
    } finally {
      this.#pending = false;
      this.querySelectorAll('button, select').forEach((control) => { control.disabled = false; });
      button.removeAttribute('aria-busy');
      label.textContent = originalLabel;
    }
  };
}

if (!customElements.get('fmr-cart-upsell')) customElements.define('fmr-cart-upsell', FmrCartUpsell);
